// Shared refresh signal across the tab subtrees.
//
// The status strip's ⟳ button + the focus auto-refresh need to make every
// self-fetching panel refetch at once, and the strip's "Updated X ago"
// label needs ONE whole-dashboard freshness marker (per-tab timestamps
// would confuse a non-technical user). This tiny context carries both:
// panels add `reloadKey` to their `useEffect` dep array (one-line change);
// `triggerReload()` bumps it + stamps `lastRefreshedAt`.
//
// No external store lib — `reloadKey` is a monotonically increasing
// counter, `lastRefreshedAt` an epoch-ms marker. `lastRefreshedAt` is
// initialised to page-open time (the panels fetch on mount), so the strip
// reads "Updated just now" on first load rather than "never".

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface RefreshState {
  /** Bump → subscribed panels (effects keyed on it) refetch. */
  reloadKey: number;
  /** Epoch-ms of the last refresh cycle; powers "Updated X ago". */
  lastRefreshedAt: number | null;
  /** reloadKey++ AND lastRefreshedAt = now. */
  triggerReload: () => void;
}

const RefreshContext = createContext<RefreshState | null>(null);

export function RefreshProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [reloadKey, setReloadKey] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(
    () => Date.now(),
  );

  const triggerReload = useCallback(() => {
    setReloadKey((k) => k + 1);
    setLastRefreshedAt(Date.now());
  }, []);

  const value = useMemo<RefreshState>(
    () => ({ reloadKey, lastRefreshedAt, triggerReload }),
    [reloadKey, lastRefreshedAt, triggerReload],
  );

  return (
    <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>
  );
}

/** Consume the shared refresh state. Throws if used outside a provider —
 * a programmer error, surfaced loudly rather than silently no-op. */
// eslint-disable-next-line react-refresh/only-export-components -- provider + hook co-located (idiomatic context module)
export function useRefresh(): RefreshState {
  const ctx = useContext(RefreshContext);
  if (ctx === null) {
    throw new Error("useRefresh must be used within a RefreshProvider");
  }
  return ctx;
}
